import React, {useContext, useEffect, useState} from 'react';
import {
    Avatar, AvatarGroup, Button, Card,
    CardActions,
    CardContent, CardHeader,
    CardMedia,
    Chip, Dialog, DialogActions, DialogContent, DialogContentText, Divider,
    Menu, MenuItem, Tooltip
} from "@mui/material";

import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import customParseFormat from 'dayjs/plugin/customParseFormat';
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import {PAContext} from "../../../Services/PAContext";
import Box from "@mui/material/Box";
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    timelineItemClasses,
    TimelineSeparator
} from "@mui/lab";
import ListItemIcon from "@mui/material/ListItemIcon";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import {capitalizeFirstLetter, formatName, stringAvatar, stringToColor} from "../../Helpers/TextStyling";
import LodgingDialogEditAttendees from "./LodgingDialogEditAttendees";
import LodgingDialogEditCosts from "./LodgingDialogEditCosts";
import LodgingDialogEditPhotos from "./LodgingDialogEditPhotos";
import LodgingDialogEditDetails from "./LodgingDialogEditDetails";
import LodgingDialogEditLocation from "./LodgingDialogEditLocation";
import LodgingDialogEditDates from "./LodgingDialogEditDates";
import {getAllContactsFromDatabase} from "../../Restricted/Contacts/BrowserDatabaseFunctions";

dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Dynamic lodging statuses matching workflow specs
const LODGING_STATUSES = [
    { value: 0, label: "💡 Researching / Shortlist", color: "#64748b", bg: "rgba(100, 116, 139, 0.1)" },
    { value: 1, label: "⏳ Hold / Payment Pending", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.1)" },
    { value: 2, label: "✅ Confirmed & Booked", color: "#10b981", bg: "rgba(16, 185, 129, 0.1)" },
    { value: 3, label: "🔑 Checked In / Active", color: "#3b82f6", bg: "rgba(59, 130, 246, 0.1)" },
    { value: 4, label: "🏁 Checked Out & Settled", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.1)" },
    { value: 5, label: "❌ Cancelled by Us", color: "#ef4444", bg: "rgba(239, 68, 68, 0.1)" },
    { value: 6, label: "🚫 Cancelled by Agent", color: "#b91c1c", bg: "rgba(185, 28, 28, 0.1)" }
];

function LodgingCard(props){

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;
        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) setDbContacts([]);
            });

        return () => { cancelled = true; };
    }, [authenticated]);

    const [ attendeesReferenceArray, setAttendeesReferenceArray ] = useState( authenticated
        ? dbContacts
        : SampleAttendees);

    useEffect(() => {
        setAttendeesReferenceArray(authenticated ? dbContacts : SampleAttendees);
    }, [authenticated, dbContacts, SampleAttendees]);

    const [eventLodging, setEventLodging] = useState(props.lodging);
    useEffect(() => {
        setEventLodging(props.lodging);
    }, [props.lodging]);

    const [eventLodgingPhotos, setEventLodgingPhotos] = useState([]);
    const [uniqueLocations, setUniqueLocations] = useState([]);
    const [lodgingLocation, setLodgingLocation] = useState(eventLodging.location);

    useEffect(() => {
        const meals = PA_GlobalObject.Planning.meals || [];
        const lodging = PA_GlobalObject.Planning.lodging || [];
        const activities = PA_GlobalObject.Planning.activities || [];

        const allLocations = [
            ...meals.map((meal) => meal.location),
            ...lodging.map((lodging) => lodging.location),
            ...activities.map((activity) => activity.location),
        ];

        const unique = Array.from(
            new Map(
                allLocations
                    .filter(location => location.name && location.google_place_id)
                    .map((location) => [location.google_place_id, location])
            ).values()
        );

        const sortedUnique = unique.sort((a, b) => {
            const nameA = (a.name || "").toLowerCase();
            const nameB = (b.name || "").toLowerCase();
            return nameA.localeCompare(nameB);
        });

        const matchingLocation = sortedUnique.filter(
            (location) => lodgingLocation && location.google_place_id === lodgingLocation.google_place_id
        );
        const otherLocations = sortedUnique.filter(
            (location) => !lodgingLocation || location.google_place_id !== lodgingLocation.google_place_id
        );

        setUniqueLocations([...matchingLocation, ...otherLocations]);
    }, []);

    function deepEqual(a, b) {
        if (a === b) return true;
        if (Number.isNaN(a) && Number.isNaN(b)) return true;
        if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
        if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;

        const aIsArr = Array.isArray(a), bIsArr = Array.isArray(b);
        if (aIsArr !== bIsArr) return false;

        if (aIsArr) {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
            return true;
        }

        const aKeys = Object.keys(a), bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        for (const k of aKeys) if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
        for (const k of aKeys) if (!deepEqual(a[k], b[k])) return false;
        return true;
    }

    function deepMergePreserveExtras(base, patch) {
        if (patch === null) return null;
        const baseIsObj = base && typeof base === "object";
        const patchIsObj = patch && typeof patch === "object";
        if (!baseIsObj || !patchIsObj) return patch;

        if (Array.isArray(base) || Array.isArray(patch)) {
            return Array.isArray(patch) ? patch.slice() : patch;
        }

        const out = { ...base };
        for (const key of Object.keys(patch)) {
            const merged = deepMergePreserveExtras(base[key], patch[key]);
            if (merged === null) {
                if (key in out) delete out[key];
            } else {
                out[key] = merged;
            }
        }
        return out;
    }

    useEffect(() => {
        const merged = deepMergePreserveExtras(eventLodging, props.lodging);
        if (!deepEqual(merged, eventLodging)) {
            setEventLodging(merged);
        }
    }, [props.lodging]);


    const handleActionClick = (_item) => {
        setAnchorLodgingListItemMenu(null);
        if (_item === 'Delete'){
            props.onDeleteLodging();
        } else if (_item === "Attendees"){
            setLodgingDialogEditAttendeesOpen(true);
        } else if (_item === "Costs") {
            setLodgingDialogEditCostsOpen(true);
        } else if (_item === "Photos") {
            setLodgingDialogEditPhotosOpen(true);
        } else if (_item === "Dates") {
            setLodgingDialogEditDatesOpen(true);
        } else if (_item === "Location") {
            setLodgingDialogEditLocationOpen(true);
        } else if (_item === "Details") {
            setLodgingDialogEditDetailsOpen(true);
        }
    };

    const [openDialogDateRange, setOpenDialogDateRange] = useState(false);
    const [anchorLodgingListItemMenu, setAnchorLodgingListItemMenu] = useState(null);

    const handleLodgingListItemMenuOpenMenu = (event) => setAnchorLodgingListItemMenu(event.currentTarget);
    const handleLodgingListItemMenuClose = () => setAnchorLodgingListItemMenu(null);

    const [isLodgingDialogEditAttendeesOpen, setLodgingDialogEditAttendeesOpen] = useState(false);
    const [isLodgingDialogEditCostsOpen, setLodgingDialogEditCostsOpen] = useState(false);
    const [isLodgingDialogEditPhotosOpen, setLodgingDialogEditPhotosOpen] = useState(false);
    const [isLodgingDialogEditDatesOpen, setLodgingDialogEditDatesOpen] = useState(false);
    const [isLodgingDialogEditLocationOpen, setLodgingDialogEditLocationOpen] = useState(false);
    const [isLodgingDialogEditDetailsOpen, setLodgingDialogEditDetailsOpen] = useState(false);

    useEffect(() => {
        if (eventLodging['location'].google_place_id && eventLodgingPhotos.length === 0) {
            fetchPlacePhotos(eventLodging['location'].google_place_id);
        }
    }, [eventLodging['location'].google_place_id]);

    const fetchPlacePhotos = async (placeId) => {
        try {
            const { Place } = await window.google.maps.importLibrary("places");
            const place = new Place({ id: placeId });
            await place.fetchFields({ fields: ['photos'] });

            if (place.photos && place.photos.length > 0) {
                const urls = place.photos.map(p => p.getURI({ maxWidth: 200, maxHeight: 150 }));
                setEventLodgingPhotos(urls);
            }
        } catch (error) {
            console.error("Error fetching place photos:", error);
        }
    };

    const userAttendee = PA_GlobalObject.Planning.attendees.find(_att => _att.id === Account.id);
    const hasEditPrivileges =
        userAttendee?.role === "planner" ||
        userAttendee?.role === "admin" ||
        eventLodging.attendees.some(_a => _a.id === Account.id);

    // Robust parsing for status configs supporting numbers and strings
    const getStatusStyles = (status) => {
        if (status === undefined || status === null) return null;

        // Handle database numbers configuration natively
        if (typeof status === 'number') {
            const found = LODGING_STATUSES.find(s => s.value === status);
            if (found) {
                return {
                    label: found.label,
                    color: "default",
                    icon: found.icon,
                    bg: found.bg,
                    textColor: found.color
                };
            }
        }

        // Backwards compatibility legacy string format parsing
        const normalized = String(status).toLowerCase();
        switch (normalized) {
            case 'confirmed':
                return { label: '✅ Confirmed', icon: 'icon-checkmark-circle', bg: 'rgba(16, 185, 129, 0.1)', textColor: '#10b981' };
            case 'tentative':
            case 'pending':
                return { label: '⏳ Tentative', icon: 'icon-history', bg: 'rgba(245, 158, 11, 0.1)', textColor: '#f59e0b' };
            case 'cancelled':
            case 'canceled':
                return { label: '❌ Cancelled', icon: 'icon-cancel-circle', bg: 'rgba(239, 68, 68, 0.1)', textColor: '#ef4444' };
            default:
                return { label: capitalizeFirstLetter(normalized), icon: 'icon-info', bg: 'rgba(241, 245, 249, 0.6)', textColor: '#64748b' };
        }
    };

    const statusConfig = getStatusStyles(eventLodging.status);

    const handleSaveLodgingUpdate = (updatedLodging) => {
        // 1. Update local component state
        setEventLodging(updatedLodging);

        // 2. Identify any new attendees needing addition to global scope
        const globalAttendees = usePA_GlobalStore.getState().PA_GlobalObject.Planning.attendees;
        const newLodgingAttendees = updatedLodging.attendees || [];

        const attendeesToAdd = newLodgingAttendees
            .filter(newAtt => !globalAttendees.some(existing => existing.id === newAtt.id))
            .map(newAtt => attendeesReferenceArray.find(ref => ref.id === newAtt.id))
            .filter(Boolean);

        // 3. Update Zustand Store ONCE on user change
        set_PA_GlobalObject((prev) => ({
            Planning: {
                ...prev.Planning,
                attendees: attendeesToAdd.length > 0
                    ? [...prev.Planning.attendees, ...attendeesToAdd]
                    : prev.Planning.attendees,
                lodging: prev.Planning.lodging.map((l) =>
                    l.id === updatedLodging.id ? { ...l, ...updatedLodging } : l
                ),
            },
        }));

        // 4. Send socket update ONCE
        if (PA_GlobalObject.Planning?.status !== 10) {
            sendSocketMessage({
                action: "E_updateEventLodging_v2",
                event_id: PA_GlobalObject.Planning.id,
                lodging: updatedLodging,
            });
        }
    };

    return (
        <div>
            <Card
                sx={{
                    borderRadius: '16px',
                    border: '1px solid rgba(240, 150, 55, 0.25)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    background: '#fff',
                    mb: 3,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
                    }
                }}
            >
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                    {eventLodgingPhotos && eventLodgingPhotos.length > 0 ? (
                        <div className="lodging-suggestions-add-list-item-photos">
                            <div className="horizontal-scroll-container">
                                {eventLodgingPhotos.map((url, index) => (
                                    <CardMedia
                                        key={index}
                                        component="img"
                                        alt={`Photo ${index + 1}`}
                                        image={url}
                                        sx={{ height: 130, width: 'auto', objectFit: 'cover' }}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="lodging-suggestions-add-list-item-photos">
                            {Object.keys(eventLodging.Duffel || {}).length > 0 ? (
                                <CardMedia
                                    sx={{ height: 130, objectFit: 'cover' }}
                                    image={eventLodging.Duffel.accommodation?.photos?.[0]?.url}
                                    title="Duffel Photo"
                                />
                            ) : Object.keys(eventLodging.Place?.photos || {}).length > 0 ? (
                                <div className="photo-placeholder" style={{ padding: '20px', textAlign: 'center', background: '#f8fafc', color: '#94a3b8', fontSize: '12px' }}>
                                    Loading Google Photos...
                                </div>
                            ) : null}
                        </div>
                    )}

                    <CardHeader
                        title={
                            (() => {
                                const timeTable = eventLodging['time_table'];
                                if (timeTable?.dates?.length > 0) {
                                    const firstDate = dayjs(timeTable.dates[0].date, "YYYY-MM-DD");
                                    const lastDate = dayjs(timeTable.dates[timeTable.dates.length - 1].date, "YYYY-MM-DD");
                                    if (firstDate.isValid() && lastDate.isValid()) {
                                        return `${firstDate.format('MMM D')} - ${lastDate.format('MMM D')}`;
                                    }
                                }
                                if (timeTable?.days?.length > 0) {
                                    const firstDayKey = Object.keys(timeTable.days[0])[0];
                                    const lastDayKey = Object.keys(timeTable.days[timeTable.days.length - 1])[0];
                                    return `${timeTable.days[0][firstDayKey]} - ${timeTable.days[timeTable.days.length - 1][lastDayKey]}`;
                                }
                                return 'Dates TBD';
                            })()
                        }
                        action={
                            hasEditPrivileges && (
                                <IconButton
                                    aria-label="settings"
                                    onClick={handleLodgingListItemMenuOpenMenu}
                                    sx={{ color: '#64748b', '&:hover': { background: '#f1f5f9' } }}
                                >
                                    <i className="icon-menu2" style={{fontSize: '16px'}} />
                                </IconButton>
                            )
                        }
                        sx={{ pb: 0, pt: 2 }}
                        titleTypographyProps={{ fontSize: '1.1rem', fontWeight: 800, color: '#1e293b' }}
                    />

                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 1, pb: 1.5 }}>
                        {(() => {
                            const timeTable = eventLodging?.time_table || {};
                            const dates = timeTable.dates || [];
                            const days = timeTable.days || [];
                            let nightText = "";

                            if (dates.length > 0) {
                                const beginDate = dayjs(dates[0]?.date);
                                const endDate = dayjs(dates[dates.length - 1]?.date);
                                if (beginDate.isValid() && endDate.isValid()) {
                                    const diff = endDate.diff(beginDate, 'day');
                                    nightText = `${diff} ${diff === 1 ? 'Night' : 'Nights'}`;
                                }
                            } else if (days.length > 0) {
                                const checkin = parseInt(Object.keys(days[0])[0].replace('day ', ''));
                                const checkout = parseInt(Object.keys([...days].reverse()[0])[0].replace('day ', ''));
                                const diff = checkout - checkin;
                                nightText = `${diff} ${diff === 1 ? 'Night' : 'Nights'}`;
                            }

                            return nightText ? (
                                <Chip
                                    icon={<i className="icon-moon" style={{ fontSize: '0.85rem', color: '#f09637' }} />}
                                    label={nightText}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 700, fontSize: '11px', borderRadius: '8px', border: '1px solid rgba(240,150,55,0.3)', color: '#f09637', background: 'rgba(240,150,55,0.03)' }}
                                />
                            ) : <div />;
                        })()}

                        {statusConfig && (
                            <Chip
                                label={statusConfig.label}
                                size="small"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: '11px',
                                    borderRadius: '8px',
                                    backgroundColor: statusConfig.bg,
                                    color: statusConfig.textColor,
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                                    border: `1px solid ${statusConfig.textColor}20`
                                }}
                            />
                        )}

                    </Box>

                    <CardContent sx={{ p: 0, pb: '40px !important' }}>
                        {(() => {
                            const tt = eventLodging?.time_table || {};
                            const dates = tt.dates || [];
                            const days = tt.days || [];
                            const hasDates = dates.length > 0;
                            const hasDays = days.length > 0;

                            const firstEntry = hasDates ? dates[0] : (hasDays ? days[0] : null);
                            const lastEntry = hasDates ? dates[dates.length - 1] : (hasDays ? days[days.length - 1] : null);
                            const isDateMode = hasDates;

                            const renderBadgeText = (entry) => {
                                if (!entry) return "TBD";
                                if (isDateMode) {
                                    return dayjs(entry.date).isValid() ? dayjs(entry.date).format('MMM D') : "TBD";
                                }
                                const dayValue = Object.values(entry)[0];
                                return typeof dayValue === 'string' ? dayValue.substring(0, 2).toUpperCase() : "TBD";
                            };

                            return (
                                <Timeline sx={{ [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 }, m: 0, px: 2, pb: 1 }}>
                                    <TimelineItem>
                                        <TimelineSeparator>
                                            <TimelineDot sx={{ border: '2px solid #e2e8f0', bgcolor: '#fff', color: '#1e293b', p: '6px', minWidth: 42, alignItems: 'center', justifyContent: 'center', boxShadow: 'none' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '10px', lineHeight: 1 }}>
                                                    {renderBadgeText(firstEntry)}
                                                </Typography>
                                            </TimelineDot>
                                            <TimelineConnector sx={{ bgcolor: '#f1f5f9', width: '2px' }} />
                                        </TimelineSeparator>
                                        <TimelineContent sx={{ py: '6px', px: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '12px', color: '#475569' }}>
                                                {firstEntry?.checkin_time || "15:00"} • Check-In
                                            </Typography>
                                            <Box sx={{ mt: 0.25 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '14px', color: '#1e293b' }}>
                                                    {eventLodging?.location?.name || eventLodging?.name || 'Unassigned Accommodation'}
                                                </Typography>
                                                {eventLodging?.location?.address && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        component="div"
                                                        sx={{ fontSize: '11px', mt: 0.25, color: '#64748b', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const fullAddress = `${eventLodging.location.address} ${eventLodging.location.city || ''} ${eventLodging.location.state || ''}`.trim();
                                                            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
                                                            window.open(mapsUrl, '_blank', 'noopener,noreferrer');
                                                        }}
                                                    >
                                                        {eventLodging.location.address}, {eventLodging.location.city || ''}{eventLodging.location.state ? ` ${eventLodging.location.state}` : ''}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TimelineContent>
                                    </TimelineItem>

                                    <TimelineItem>
                                        <TimelineSeparator>
                                            <TimelineDot sx={{ border: '2px solid rgba(240,150,55,0.15)', bgcolor: '#fff', color: '#f09637', p: '6px', minWidth: 42, alignItems: 'center', justifyContent: 'center', boxShadow: 'none' }}>
                                                <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '10px', lineHeight: 1 }}>
                                                    {renderBadgeText(lastEntry)}
                                                </Typography>
                                            </TimelineDot>
                                        </TimelineSeparator>
                                        <TimelineContent sx={{ py: '6px', px: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '12px', color: '#475569' }}>
                                                {lastEntry?.checkout_time || "11:00"} • Check-Out
                                            </Typography>
                                        </TimelineContent>
                                    </TimelineItem>
                                </Timeline>
                            );
                        })()}

                        {eventLodging?.location?.website && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mt: -2 }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<i className="icon-link" style={{ fontSize: '11px' }} />}
                                    onClick={() => window.open(eventLodging.location.website, "_blank")}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '11px',
                                        color: '#64748b',
                                        '&:hover': { color: '#f09637', backgroundColor: 'transparent' }
                                    }}
                                >
                                    Visit Website
                                </Button>
                            </Box>
                        )}

                        {(
                            eventLodging.notes ||
                            eventLodging.booked_through ||
                            eventLodging.confirmation_number ||
                            eventLodging.reservation_name ||
                            eventLodging.reservation_number ||
                            eventLodging.door_code ||
                            eventLodging.gate_code
                        ) && (
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.45)',
                                    border: '1px solid rgba(255,255,255,0.5)',
                                    borderRadius: '12px',
                                    p: 1.5,
                                    mt: 1.5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1
                                }}
                            >
                                {eventLodging.notes?.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                        <strong style={{ color: '#2c3e50' }}>Notes:</strong> {eventLodging.notes}
                                    </Typography>
                                )}
                                {eventLodging.booked_through?.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                        <strong style={{ color: '#2c3e50' }}>Booked Through:</strong> {eventLodging.booked_through}
                                    </Typography>
                                )}
                                {eventLodging.confirmation_number?.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                        <strong style={{ color: '#2c3e50' }}>Confirmation#:</strong> {eventLodging.confirmation_number}
                                    </Typography>
                                )}
                                {eventLodging.reservation_name?.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                        <strong style={{ color: '#2c3e50' }}>Reservation Name:</strong> {eventLodging.reservation_name}
                                    </Typography>
                                )}
                                {eventLodging.reservation_number?.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                        <strong style={{ color: '#2c3e50' }}>Reservation#:</strong> {eventLodging.reservation_number}
                                    </Typography>
                                )}
                                {eventLodging.gate_code?.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                        <strong style={{ color: '#2c3e50' }}>Gate Code:</strong> {eventLodging.gate_code}
                                    </Typography>
                                )}
                                {eventLodging.door_code?.length > 0 && (
                                    <Typography variant="caption" sx={{ display: "block" }} color="text.secondary">
                                        <strong style={{ color: '#2c3e50' }}>Door Code:</strong> {eventLodging.door_code}
                                    </Typography>
                                )}
                            </Box>
                        )}


                    </CardContent>

                    <CardActions sx={{ position: 'absolute', bottom: '8px', right: '12px', p: 0 }}>
                        <AvatarGroup
                            max={10}
                            sx={{
                                '.MuiAvatar-root': {
                                    zIndex: (theme) => theme.zIndex.drawer + 1,
                                    marginRight: '-6px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    color: 'text.primary',
                                    fontWeight: '600',
                                    fontSize: '12px',
                                    width: '28px',
                                    height: '28px',
                                }
                            }}
                        >
                            {eventLodging.attendees
                                .sort((a, b) => {
                                    const a_attendee = PA_GlobalObject.Planning.attendees.find((_att) => _att.id === a.id);
                                    const b_attendee = PA_GlobalObject.Planning.attendees.find((_att) => _att.id === b.id);
                                    if (!a_attendee && !b_attendee) return 0;
                                    if (!a_attendee) return 1;
                                    if (!b_attendee) return -1;
                                    const aHasNickname = a_attendee.nickname?.trim() || '';
                                    const bHasNickname = b_attendee.nickname?.trim() || '';
                                    return aHasNickname && bHasNickname
                                        ? a_attendee.nickname.toLowerCase().localeCompare(b_attendee.nickname.toLowerCase())
                                        : (a_attendee.fname || '').localeCompare(b_attendee.fname || '');
                                })
                                .map((_a) => {
                                    const attendee = PA_GlobalObject.Planning.attendees.find((_att) => _att.id === _a.id);
                                    const attendeeAvatar = attendeesReferenceArray.find((_att) => _att.id === _a.id);
                                    if (!attendee) return null;

                                    const fullName = attendee.nickname?.trim()
                                        ? `${formatName(attendee.nickname)}`
                                        : `${formatName(attendee.fname)} ${formatName(attendee.lname)}`;

                                    const customAvatarStyles = {
                                        border: `1.5px solid ${stringToColor(fullName)}`,
                                    };

                                    return (
                                        <Tooltip key={_a.id} title={fullName} arrow>
                                            {!attendeeAvatar?.avatar || !attendeeAvatar?.avatar.preview ? (
                                                <Avatar
                                                    {...stringAvatar(`${formatName(attendee.fname)}, ${formatName(attendee.lname)}`)}
                                                    sx={customAvatarStyles}
                                                />
                                            ) : (
                                                <Avatar
                                                    src={attendeeAvatar.avatar.preview}
                                                    sx={customAvatarStyles}
                                                />
                                            )}
                                        </Tooltip>
                                    );
                                })}
                        </AvatarGroup>
                    </CardActions>
                </Box>

                {/* Premium Action Menu */}
                <Menu
                    anchorEl={anchorLodgingListItemMenu}
                    open={Boolean(anchorLodgingListItemMenu)}
                    onClose={handleLodgingListItemMenuClose}
                    PaperProps={{ sx: { borderRadius: '14px', width: 240, mt: 1, p: 0.5, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' } }}
                >
                    <Box sx={{ px: 2, py: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '10px', fontWeight: 900, color: '#94a3b8', letterSpacing: 1 }}>SETTINGS</Typography>
                    </Box>
                    {[
                        { key: 'Location', icon: 'icon-map-marker', label: 'Location', color: '#ff6f61' },
                        { key: 'Dates', icon: 'icon-calendar2', label: 'Dates', color: '#d4af37' },
                        { key: 'Costs', icon: 'icon-wallet', label: 'Costs', color: '#2e8b57' },
                        { key: 'Attendees', icon: 'icon-group', label: 'Attendees', color: '#10318f' },
                        { key: 'Details', icon: 'icon-gear', label: 'Details', color: '#6c757d' },
                        { key: 'Photos', icon: 'icon-picture2', label: 'Photos', color: '#8a2be2' },
                    ].map((item) => (
                        <MenuItem key={item.key} onClick={() => handleActionClick(item.label)} sx={{ borderRadius: '8px', mb: 0.2 }}>
                            <ListItemIcon>
                                <Box sx={{ width: 30, height: 30, bgcolor: `${item.color}15`, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className={item.icon} style={{ color: item.color, fontSize: '14px' }} />
                                </Box>
                            </ListItemIcon>
                            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
                        </MenuItem>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <MenuItem onClick={() => handleActionClick('Delete')} sx={{ borderRadius: '8px', color: '#ef4444' }}>
                        <ListItemIcon>
                            <Box sx={{ width: 30, height: 30, bgcolor: '#fee2e2', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="icon-trash" style={{ color: '#ef4444', fontSize: '14px' }} />
                            </Box>
                        </ListItemIcon>
                        <ListItemText primary="Delete" primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }} />
                    </MenuItem>
                </Menu>



            </Card>

            <Dialog open={openDialogDateRange} onClose={() => setOpenDialogDateRange(false)}>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        The date for the Lodging is outside of the date range for the Event.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialogDateRange(false)} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <LodgingDialogEditAttendees
                isOpen={isLodgingDialogEditAttendeesOpen}
                onClose={() => setLodgingDialogEditAttendeesOpen(false)}
                eventLodging={eventLodging}
                setEventLodging={handleSaveLodgingUpdate}
            />

            <LodgingDialogEditCosts
                isOpen={isLodgingDialogEditCostsOpen}
                onClose={() => setLodgingDialogEditCostsOpen(false)}
                eventLodging={eventLodging}
                setEventLodging={handleSaveLodgingUpdate}
            />

            <LodgingDialogEditPhotos
                isOpen={isLodgingDialogEditPhotosOpen}
                onClose={() => setLodgingDialogEditPhotosOpen(false)}
                eventLodging={eventLodging}
                setEventLodging={handleSaveLodgingUpdate}
            />

            <LodgingDialogEditDates
                isOpen={isLodgingDialogEditDatesOpen}
                onClose={() => setLodgingDialogEditDatesOpen(false)}
                eventLodging={eventLodging}
                setEventLodging={handleSaveLodgingUpdate}
            />

            <LodgingDialogEditLocation
                isOpen={isLodgingDialogEditLocationOpen}
                onClose={() => setLodgingDialogEditLocationOpen(false)}
                eventLodging={eventLodging}
                setEventLodging={handleSaveLodgingUpdate}
            />

            <LodgingDialogEditDetails
                isOpen={isLodgingDialogEditDetailsOpen}
                onClose={() => setLodgingDialogEditDetailsOpen(false)}
                eventLodging={eventLodging}
                setEventLodging={handleSaveLodgingUpdate}
            />
        </div>
    );
}

export default LodgingCard;